import { ReactElement, ReactNode } from 'react';

import SolstenLogo from 'ui-lib-12traits/src/Icons/svg/common/solsten-logo.svg?component';

import Footer from '@/layouts/Footer';

import { REACTIVATE_EMAIL, SavedAudience } from './helpers';
import styles from './UpgradeOverlay.module.scss';

// No scheduler yet: booking the walkthrough emails Victor.
const BOOK_HREF = `mailto:${REACTIVATE_EMAIL}?subject=${encodeURIComponent(
  'Book my 30-min Solsten walkthrough'
)}`;

// The card shows the account's first few audiences, with the total beside it.
const AUDIENCES_SHOWN = 3;

// Icons from the founder's mockup, drawn in currentColor.
function Icon({ size = 20, children }: { size?: number; children: ReactNode }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const lock = (
  <>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>
);
const arrow = (
  <>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </>
);
const check = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </>
);
const people = (
  <>
    <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
    <circle cx="10" cy="8" r="3.5" />
    <path d="M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35" />
    <path d="M15.5 4.6a3.5 3.5 0 0 1 0 6.8" />
  </>
);
const sparkle = (
  <>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </>
);

const CHANGES: { icon: ReactNode; title: string; before: string; now: string }[] = [
  {
    icon: people,
    title: 'Audiences',
    before: 'Only the audiences you commissioned',
    now: '100,000+ ready-made audiences from 50,000+ games, plus yours'
  },
  {
    icon: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
    title: 'Insights',
    before: 'Static persona and psychology reports',
    now: 'Elaris, an AI agent that answers as the digital twins of your audience'
  },
  {
    icon: (
      <>
        <path d="M9 3h6" />
        <path d="M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" />
      </>
    ),
    title: 'Testing ideas',
    before: 'Commission a new study and wait',
    now: 'Test concepts, features and messaging with your audience before you spend'
  },
  {
    icon: (
      <>
        <path d="m12 3 9 5-9 5-9-5z" />
        <path d="m3 13 9 5 9-5" />
      </>
    ),
    title: 'Platform',
    before: 'Traits, Navigator and Elaris in separate places',
    now: 'One platform: launch Traits assessments, explore audiences and compare markets'
  },
  {
    icon: (
      <>
        <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
        <circle cx="8" cy="8" r="1.5" />
      </>
    ),
    title: 'Pricing',
    before: '$10k+ for a single custom audience',
    now: 'Team plans from 3 seats, with custom audience packages that scale with you'
  }
];

const DIFFERENCES: { icon: ReactNode; tone: string; title: string; body: string }[] = [
  {
    icon: (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    tone: styles.toneBlue,
    title: 'Understand your audiences',
    body: 'See what drives them, in the context of your brand and goals.'
  },
  {
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2 5-5 2 2-5z" />
      </>
    ),
    tone: styles.toneTeal,
    title: 'Discover untapped opportunities',
    body: 'Explore new audiences, compare markets and size growth potential.'
  },
  {
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
    tone: styles.tonePurple,
    title: 'Turn insights into action',
    body: 'Test ideas with Elaris and shape strategy before you commit budget.'
  }
];

const STEPS: { title: string; body: string }[] = [
  {
    title: 'We open your saved audiences',
    body: 'See the audiences you built with us, live inside the new platform.'
  },
  {
    title: 'You put Elaris to work',
    body: 'Bring a concept, feature or campaign you’re weighing. We’ll test it with your audience’s digital twin, on the call.'
  },
  {
    title: 'We map a plan to your team',
    body: 'Seats, audience packages and Traits assessments sized to your roadmap. Nothing to decide on the call.'
  }
];

const RESULTS: { value: string; claim: string; company: string }[] = [
  { value: '70%', claim: 'lower CPI than genre average', company: 'Mobilityware' },
  { value: '22%', claim: 'more daily sessions', company: 'Supercell' },
  { value: '60%', claim: 'reduced acquisition costs for playtests', company: 'Playable Worlds' }
];

const FAQ: { question: string; answer: string }[] = [
  {
    question: 'Why can’t I see my dashboard?',
    answer:
      'Your Solsten subscription wasn’t renewed, so access is paused. Your account is still here, and so is everything you built.'
  },
  {
    question: 'Are my audiences still saved?',
    answer:
      'Yes. The audiences you built with us are waiting, and they come with you into the new platform when you reactivate.'
  },
  {
    question: 'What does the new Solsten cost?',
    answer:
      'Plans start at 3 seats and scale with custom audience packages. Every plan includes the library of 100,000+ ready-made audiences and Elaris. For context, a single custom audience used to run $10k+. We’ll size the right package on the call.'
  },
  {
    question: 'Do I have to commit to anything on the call?',
    answer:
      'No. It’s a working session, not a slide deck. If it isn’t a fit, you’ll still leave with a read on one idea from your own audience.'
  },
  {
    question: 'How is Elaris different from AI persona tools?',
    answer:
      'Most persona tools generate synthetic people from reports and model inference. Elaris answers from digital twins grounded in 2M+ psychological profiles from real people’s assessments.'
  },
  {
    question: 'Can my whole team get access?',
    answer:
      'Yes. Plans are built around seats, so research, marketing and product can all work from the same audiences.'
  }
];

const STATS: { value: string; body: string }[] = [
  { value: '2M+', body: 'psychological profiles from real people power every digital twin' },
  { value: '100,000+', body: 'ready-made audiences to explore, plus the ones you built' },
  { value: '50,000+', body: 'unique games, so you can compare any market or genre' }
];

const CLIENTS = ['Zynga', 'EA', 'Sony', 'Meta', 'LEGO', 'Peloton', 'DraftKings'];

// Blurred bars standing in for each audience's locked data.
const BARS = [
  [86, 58, 72],
  [64, 90, 44],
  [78, 36, 82]
];
const AUDIENCE_TONES = [styles.toneBlue, styles.toneTeal, styles.tonePurple];

function BookLink({ dark = false }: { dark?: boolean }): ReactElement {
  return (
    <a className={dark ? `${styles.book} ${styles.bookOnDark}` : styles.book} href={BOOK_HREF}>
      Book my 30-min walkthrough
      <Icon>{arrow}</Icon>
    </a>
  );
}

function SavedAudiences({ audiences }: { audiences: SavedAudience[] }): ReactElement {
  return (
    <section className={styles.audiences} aria-labelledby="lapsed-saved-audiences">
      <div className={styles.audiencesHeader}>
        <div>
          <h2 id="lapsed-saved-audiences" className={styles.audiencesTitle}>
            Your saved audiences
          </h2>
          <p className={styles.audiencesNote}>
            <Icon size={13}>{lock}</Icon>
            Unlocks when you reactivate
          </p>
        </div>
        <span className={styles.savedCount}>{audiences.length} saved</span>
      </div>
      <ul className={styles.audienceList}>
        {audiences.slice(0, AUDIENCES_SHOWN).map((audience, i) => (
          <li key={audience.id} className={styles.audience}>
            <span className={`${styles.audienceIcon} ${AUDIENCE_TONES[i]}`}>
              <Icon>{people}</Icon>
            </span>
            <span className={styles.audienceText}>
              <span className={styles.audienceName}>{audience.name}</span>
              <span className={styles.audienceLabel}>{audience.label}</span>
            </span>
            <span className={styles.bars} aria-hidden="true">
              <span style={{ width: `${BARS[i][0]}%` }} />
              <span style={{ width: `${BARS[i][1]}%` }} />
              <span style={{ width: `${BARS[i][2]}%` }} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// What a locked account (see helpers.ts) gets instead of the platform: the
// founder's lapsed account page (desktop, responsive), with every booking
// button emailing Victor until there's a scheduler.
export default function UpgradeOverlay({
  audiences,
  onLogOut
}: {
  // The account's dashboards; the card is left out when there are none.
  audiences: SavedAudience[];
  onLogOut: () => void;
}): ReactElement {
  const firstAudience = audiences[0]?.name;

  return (
    <div className={styles.page}>
      {/* The platform's own header (as BaseHeader) and footer. */}
      <header className={styles.header}>
        <SolstenLogo className={styles.logo} role="img" aria-label="Solsten" />
        <div className={styles.headerActions}>
          <span className={styles.paused}>
            <Icon size={14}>{lock}</Icon>
            Account paused
          </span>
          <button className={styles.logOut} type="button" onClick={onLogOut}>
            Log out
          </button>
        </div>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.ended}>
              <span className={styles.endedDot} aria-hidden="true" />
              Your Solsten subscription has ended
            </p>
            <h1 className={styles.heroTitle}>
              Your audiences are still here. Now they can talk back.
            </h1>
            <p className={styles.heroBody}>
              Your subscription wasn’t renewed, so your dashboard is paused — but every audience you
              built with us is saved. Since you’ve been away, Traits, Navigator and Elaris have
              become one platform, where those audiences become digital twins you can question, test
              ideas with, and compare against 100,000+ others.
            </p>
            <div className={styles.heroActions}>
              <BookLink />
              <a className={styles.whatsNew} href="#whats-new">
                See what’s new first
              </a>
            </div>
            <ul className={styles.promises}>
              {['30 minutes', 'Your own audiences, live', 'No commitment'].map(promise => (
                <li key={promise}>
                  <Icon size={18}>{check}</Icon>
                  {promise}
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.heroVisual}>
            {audiences.length > 0 && <SavedAudiences audiences={audiences} />}
            <div
              className={
                audiences.length > 0 ? styles.elaris : `${styles.elaris} ${styles.elarisAlone}`
              }
              aria-hidden="true"
            >
              <div className={styles.elarisHeader}>
                <Icon size={18}>{sparkle}</Icon>
                <span className={styles.elarisName}>Elaris</span>
                <span className={styles.elarisTwin}>
                  · digital twin of {firstAudience ?? 'your audience'}
                </span>
              </div>
              <div className={styles.elarisQuestion}>
                Would this audience pay for a season pass?
              </div>
              <div className={styles.elarisAnswer}>
                Likely, if it rewards skill rather than time played. This group values visible
                mastery far more than cosmetics.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.proof}>
        <div className={styles.inner}>
          <div className={styles.stats}>
            {STATS.map(stat => (
              <div key={stat.value} className={styles.stat}>
                <div className={styles.statValue}>{stat.value}</div>
                <div className={styles.statBody}>{stat.body}</div>
              </div>
            ))}
          </div>
          <div className={styles.clients}>
            <span className={styles.clientsLabel}>Trusted by teams at</span>
            {CLIENTS.map(client => (
              <span key={client} className={styles.client}>
                {client}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="whats-new" className={`${styles.section} ${styles.tinted}`}>
        <div className={styles.inner}>
          <div className={styles.sectionHead}>
            <p className={styles.eyebrow}>What’s changed since you’ve been away</p>
            <h2 className={styles.sectionTitle}>
              One audience used to cost $10k+. Now 100,000+ are waiting for you.
            </h2>
          </div>
          <ul className={styles.changes}>
            {CHANGES.map(change => (
              <li key={change.title} className={styles.change}>
                <div className={styles.changeTitle}>
                  <Icon size={22}>{change.icon}</Icon>
                  {change.title}
                </div>
                <div className={styles.before}>
                  <div className={styles.changeTag}>Before</div>
                  <div>{change.before}</div>
                </div>
                <div className={styles.now}>
                  <div className={styles.changeTag}>Now</div>
                  <div>{change.now}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={styles.section}>
        <div className={`${styles.inner} ${styles.split}`}>
          <div className={styles.splitCopy}>
            <p className={styles.eyebrow}>Why Elaris is different</p>
            <h2 className={styles.sectionTitle}>
              Not synthetic personas. Digital twins of real people.
            </h2>
            <p className={styles.lead}>
              Most AI persona tools stitch together reports, reviews and model guesswork. Elaris
              answers from digital twins built on 2M+ psychological profiles from real people’s
              assessments. So when you ask why players churn or which concept will land, the answer
              traces back to how real people think.
            </p>
          </div>
          <ul className={styles.differences}>
            {DIFFERENCES.map(difference => (
              <li key={difference.title} className={styles.difference}>
                <span className={`${styles.differenceIcon} ${difference.tone}`}>
                  <Icon size={22}>{difference.icon}</Icon>
                </span>
                <div>
                  <div className={styles.cardTitle}>{difference.title}</div>
                  <div className={styles.cardBody}>{difference.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="book" className={`${styles.section} ${styles.booking}`}>
        <div className={styles.inner}>
          <div className={styles.sectionHead}>
            <p className={styles.eyebrow}>Your walkthrough</p>
            <h2 className={styles.sectionTitle}>
              30 minutes. Your audiences. A real answer to a real question.
            </h2>
          </div>
          <div className={styles.split}>
            <div className={styles.steps}>
              <ol className={styles.stepList}>
                {STEPS.map((step, i) => (
                  <li key={step.title} className={styles.step}>
                    <span className={styles.stepNumber} aria-hidden="true">
                      {i + 1}
                    </span>
                    <div>
                      <div className={styles.stepTitle}>{step.title}</div>
                      <div className={styles.stepBody}>{step.body}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <div className={styles.promise}>
                <Icon size={22}>{sparkle}</Icon>
                <p>
                  <strong>Whatever you decide,</strong> you’ll leave with a read on one real idea
                  from your own audience.
                </p>
              </div>
            </div>
            <div className={styles.bookCard}>
              <div>
                <div className={styles.bookCardTitle}>Pick a time that works</div>
                <p className={styles.bookCardMeta}>
                  <Icon size={15}>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </Icon>
                  30 min · Video call
                </p>
              </div>
              <p className={styles.bookCardBody}>
                Email us the times that suit you and we’ll send a calendar invite.
              </p>
              <BookLink />
              <div className={styles.host}>
                <span className={styles.hostAvatar} aria-hidden="true" />
                Hosted by a Solsten insights specialist. You’ll get a calendar invite and a reminder
                the day before.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={`${styles.inner} ${styles.split}`}>
          <figure className={styles.quote}>
            <div className={styles.quoteMark} aria-hidden="true">
              “
            </div>
            <blockquote>
              Solsten is like having a better pair of glasses — it sharpens your perspective on
              everything.
            </blockquote>
            <figcaption>
              <strong>Raph Koster</strong>, CEO, Playable Worlds
            </figcaption>
          </figure>
          <ul className={styles.results}>
            {RESULTS.map(result => (
              <li key={result.value} className={styles.result}>
                <span className={styles.resultValue}>{result.value}</span>
                <span className={styles.resultBody}>
                  <strong>{result.claim}</strong> · {result.company}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`${styles.section} ${styles.tinted}`}>
        <div className={styles.inner}>
          <h2 className={styles.sectionTitle}>Questions you might have</h2>
          <ul className={styles.faq}>
            {FAQ.map(item => (
              <li key={item.question} className={styles.faqItem}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={styles.final}>
        <div className={styles.finalInner}>
          <h2>Your audiences are one call away.</h2>
          <p>Book 30 minutes and hear what they’ve been waiting to tell you.</p>
          <div className={styles.finalActions}>
            <BookLink dark />
            <button
              className={`${styles.textButton} ${styles.textButtonOnDark}`}
              type="button"
              onClick={onLogOut}
            >
              Log out
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
